import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { api } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { NotificationBell } from "./NotificationBell";
import { InstallButton } from "./InstallButton";
import { LayoutDashboard, Users, CalendarCheck, CalendarDays, Bot, Share2, Settings, CreditCard, LogOut, Loader2, Menu, AlertTriangle, Tag, BarChart3, FileText } from "lucide-react";
import { Button } from "./ui/button";
import { SupportBot } from "./SupportBot";
import { EnableNotifications } from "./EnableNotifications";
import SubscribeHardSell from "./SubscribeHardSell";

const nav = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/dashboard/leads", label: "Leads", icon: Users },
  { to: "/dashboard/bookings", label: "Bookings", icon: CalendarCheck },
  { to: "/dashboard/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/dashboard/invoices", label: "Invoices", icon: FileText, pro: true },
  { to: "/dashboard/receptionist", label: "Receptionist", icon: Bot },
  { to: "/dashboard/connect", label: "Connect", icon: Share2 },
  { to: "/dashboard/prices", label: "Prices", icon: Tag },
  { to: "/dashboard/billing", label: "Billing", icon: CreditCard },
  { to: "/dashboard/settings", label: "Settings", icon: Settings },
];

const bottomNav = nav.slice(0, 5); // Overview, Leads, Bookings, Calendar, Invoices

const trialDaysLeft = (company) => {
  if (!company?.trial_ends_at) return 0;
  const diff = new Date(company.trial_ends_at) - new Date();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

export default function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser, logout } = useAuth();
  const [checking, setChecking] = useState(true);
  const [company, setCompany] = useState(null);
  const [open, setOpen] = useState(false);

  const loadCompany = async () => {
    try { const res = await api.get("/company/me"); setCompany(res.data); return res.data; }
    catch { return null; }
  };

  useEffect(() => {
    const init = async () => {
      let u = user || location.state?.user;
      if (!u) {
        try { const res = await api.get("/auth/me"); u = res.data; setUser(res.data); }
        catch { navigate("/"); return; }
      }
      const c = await loadCompany();
      if (!c) { navigate("/onboarding"); return; }
      setChecking(false);
      try {
        if (localStorage.getItem("td_post_onboard") === "billing") {
          localStorage.removeItem("td_post_onboard");
          navigate("/dashboard/billing");
        }
      } catch { /* ignore */ }
    };
    init();
  }, []); // eslint-disable-line

  const doLogout = async () => { await logout(); navigate("/"); };

  if (checking) return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const daysLeft = trialDaysLeft(company);
  const showTrial = company?.subscription_status === "trial" && !company?.free_access;
  const locked = !company?.has_access;
  const trialExpired = locked && company?.subscription_status === "trial";

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      <aside className={`fixed lg:static z-50 h-screen w-64 bg-[#0f0f11] border-r border-white/10 flex flex-col transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="flex items-center gap-2.5 px-5 py-5 border-b border-white/10" style={{ paddingTop: "calc(1.25rem + env(safe-area-inset-top))" }}>
          <div className="h-8 w-8 bg-primary rounded-sm flex items-center justify-center"><Bot className="h-4 w-4 text-white" /></div>
          <span className="font-display text-xl tracking-wide">TRADIE<span className="text-primary">DESK</span></span>
        </div>
        <div className="px-4 py-3 border-b border-white/10">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Business</p>
          <p className="font-medium truncate">{company?.name}</p>
          {company?.trade && <p className="text-xs text-primary">{company.trade}</p>}
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {[...nav, ...(company?.free_access ? [{ to: "/dashboard/members", label: "Sign-ups", icon: BarChart3 }] : [])].map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)} data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-[background-color,color] duration-200 ${isActive ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground hover:bg-secondary"}`}>
              <n.icon className="h-4 w-4" /> {n.label}
              {n.pro && !company?.is_pro && <span className="ml-auto text-[9px] font-bold uppercase tracking-wider bg-primary/20 text-primary px-1.5 py-0.5 rounded">Pro</span>}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 py-2 mb-2">
            {user?.picture ? <img src={user.picture} alt="" className="h-8 w-8 rounded-sm object-cover" /> : <div className="h-8 w-8 rounded-sm bg-secondary flex items-center justify-center text-sm font-semibold">{user?.name?.[0]}</div>}
            <div className="min-w-0"><p className="text-sm font-medium truncate">{user?.name}</p><p className="text-xs text-muted-foreground truncate">{user?.email}</p></div>
          </div>
          <InstallButton always showLogo label="Install the app" variant="outline" className="sm:hidden w-full justify-start border-primary/40 text-primary hover:bg-primary/10 mb-2" />
          <Button data-testid="logout-btn" onClick={doLogout} variant="outline" className="w-full rounded-sm border-white/10 text-muted-foreground hover:text-foreground gap-2 justify-start"><LogOut className="h-4 w-4" /> Sign out</Button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setOpen(false)} />}

      <main className="flex-1 min-w-0">
        <div className="flex items-center gap-3 px-4 lg:px-8 py-3 border-b border-white/10 bg-[#0f0f11] justify-between" style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top))" }}>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setOpen(true)} className="rounded-sm lg:hidden"><Menu className="h-5 w-5" /></Button>
            <span className="font-display text-lg tracking-wide lg:hidden">TRADIEDESK</span>
            <span className="hidden lg:block text-sm text-muted-foreground">Welcome back, {user?.name?.split(" ")[0]}</span>
          </div>
          <div className="flex items-center gap-3">
            <EnableNotifications />
            <InstallButton always showLogo label="Install app" className="hidden sm:inline-flex border-white/10 text-muted-foreground hover:text-foreground" />
            <NotificationBell />
          </div>
        </div>

        {locked && (
          <div className="bg-destructive/15 border-b border-destructive/40 px-5 md:px-8 py-3 flex items-center justify-between gap-4" data-testid="locked-banner">
            <p className="text-sm flex items-center gap-2 text-red-300"><AlertTriangle className="h-4 w-4" /> {trialExpired ? "Your trial has ended. Subscribe to keep Ava answering." : company?.trial_blocked ? "A free trial isn't available for this account. Subscribe to activate Ava." : "Subscribe to switch Ava on and unlock your dashboard."}</p>
            <Button size="sm" onClick={() => navigate("/dashboard/billing")} className="rounded-sm bg-primary hover:bg-orange-600 text-white shrink-0">Subscribe</Button>
          </div>
        )}
        {!locked && showTrial && (
          <div className="bg-primary/10 border-b border-primary/30 px-5 md:px-8 py-2.5 flex items-center justify-between gap-4" data-testid="trial-banner">
            <p className="text-sm text-primary">Free trial active — {daysLeft} day{daysLeft !== 1 ? "s" : ""} left.</p>
            <Button size="sm" variant="ghost" onClick={() => navigate("/dashboard/billing")} className="rounded-sm text-primary h-8">Upgrade now</Button>
          </div>
        )}

        <div className="px-5 pt-5 md:px-8 md:pt-8 pb-24 lg:pb-8 max-w-7xl mx-auto">
          {locked ? <Paywall blocked={company?.trial_blocked} expired={trialExpired} currency={company?.display_currency} /> : <Outlet context={{ company, reloadCompany: loadCompany }} />}
        </div>
      </main>

      {!locked && !open && (
        <nav className="fixed bottom-0 inset-x-0 z-30 lg:hidden bg-[#0f0f11]/95 backdrop-blur border-t border-white/10 flex" style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="mobile-tabbar">
          {bottomNav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`tab-${n.label.toLowerCase()}`}
              className={({ isActive }) => `flex-1 flex flex-col items-center justify-center gap-1 py-2.5 text-[11px] font-medium transition-colors active:scale-95 ${isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
              <n.icon className="h-5 w-5" /> {n.label}
            </NavLink>
          ))}
        </nav>
      )}
      <SupportBot />
    </div>
  );
}

function Paywall({ blocked, expired, currency }) {
  return (
    <div className="py-6" data-testid="paywall">
      <SubscribeHardSell
        currency={currency}
        heading={expired ? "YOUR TRIAL HAS ENDED" : "SUBSCRIBE TO GET STARTED"}
        subheading={expired
          ? "Your free trial is over and Ava has stopped answering. Subscribe now to switch her back on and unlock your dashboard."
          : blocked
            ? "A free trial has already been used for this account or device. Subscribe to activate Ava and unlock your dashboard."
            : "You're one step away. Subscribe to switch Ava on full-time and unlock your dashboard."}
      />
    </div>
  );
}
